import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  Toolbar, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  MenuItem, IconButton, Tooltip, Checkbox, TablePagination, InputAdornment,
  Chip, Divider
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useDebounce } from "../../hooks/useDebounce";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";

// ─── Constants ────────────────────────────────────────────────────────────────

const NOTIFICATION_TYPES = [1, 2, 3, 4, 5];

const PRIORITY_CONFIG = {
  2: { label: "High",   color: "#DF3C26" },
  1: { label: "Medium", color: "#2E8ECD" },
  0: { label: "Low",    color: "#39BA9B" },
};

// Types that require an image_url
const TYPES_WITH_IMAGE = [2, 3, 4, 5];
// Types that require CTA buttons
const TYPES_WITH_CTA = [4, 5];

const EMPTY_FORM = {
  type: 1,
  priority: 0,
  title: "",
  description: "",
  image_url: "",
  cta: [],
  status: "ACTIVE",
};

const EMPTY_CTA = { cta_title: "", cta_url: "" };

// ─── Helper components ────────────────────────────────────────────────────────

function PriorityChip({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG[0];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.color,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.7rem",
        minWidth: 64,
      }}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NotificationsPage() {
  // ── Permission flags (read from JWT permissions, stored post-login) ──────────
  // We derive action permissions from the user's permission list returned by the API.
  // usePermissions already fetched these; we re-check them here for fine-grained
  // control over individual action buttons inside the page.
  const [userPerms, setUserPerms] = useState([]);

  useEffect(() => {
    api.get("/api/test/permissions")
      .then(({ data }) => setUserPerms(data.permissions || []))
      .catch(() => setUserPerms([]));
  }, []);

  const hasPermission = (pattern) => userPerms.some(p => new RegExp(pattern).test(p));
  const canCreate = hasPermission("^create_notification$") || hasPermission("^SUPER ADMIN$");
  const canUpdate = hasPermission("^update_notification$") || hasPermission("^SUPER ADMIN$");
  const canDelete = hasPermission("^delete_notification$") || hasPermission("^SUPER ADMIN$");

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Pagination & Search & Selection ──────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [selected, setSelected] = useState([]);

  // ── Dialog state ─────────────────────────────────────────────────────────────
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(EMPTY_FORM);

  // ── Loading / Delete states ───────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // null = bulk delete
  const [deleting, setDeleting] = useState(false);

  // ── Snackbar ──────────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/notifications", { params: { page: "all" } });
      const fetched = res.data?.data?.items || res.data?.data || [];
      setNotifications(Array.isArray(fetched) ? fetched : []);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      showSnackbar("Failed to load notifications.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ── Filtering ─────────────────────────────────────────────────────────────────
  const filteredNotifications = useMemo(() => {
    if (!debouncedSearchQuery) return notifications;
    const q = debouncedSearchQuery.toLowerCase();
    return notifications.filter(n =>
      (n.title && n.title.toLowerCase().includes(q)) ||
      (n.description && n.description.toLowerCase().includes(q))
    );
  }, [notifications, debouncedSearchQuery]);

  useEffect(() => { setPage(0); }, [debouncedSearchQuery]);

  const displayedNotifications = filteredNotifications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ── Selection ─────────────────────────────────────────────────────────────────
  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      setSelected(displayedNotifications.map(n => n.n_id));
    } else {
      setSelected([]);
    }
  };

  const handleRowCheck = (e, id) => {
    const idx = selected.indexOf(id);
    if (idx === -1) setSelected(prev => [...prev, id]);
    else setSelected(prev => prev.filter(s => s !== id));
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // ── Pagination ────────────────────────────────────────────────────────────────
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── Form helpers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "type") {
        const newType = Number(value);
        if (!TYPES_WITH_CTA.includes(newType)) {
          // Types 1–3: no CTA needed
          updated.cta = [];
        } else if (newType === 5) {
          // Type 5: exactly one fixed CTA — seed with one empty entry if needed
          updated.cta = prev.cta.length > 0 ? [prev.cta[0]] : [{ ...EMPTY_CTA }];
        }
        // Type 4: keep existing CTA list as-is (supports multiple)
      }
      return updated;
    });
  };

  const handleCtaChange = (index, field, value) => {
    setFormData(prev => {
      const cta = [...prev.cta];
      cta[index] = { ...cta[index], [field]: value };
      return { ...prev, cta };
    });
  };

  const addCta = () =>
    setFormData(prev => ({ ...prev, cta: [...prev.cta, { ...EMPTY_CTA }] }));

  const removeCta = (index) =>
    setFormData(prev => ({ ...prev, cta: prev.cta.filter((_, i) => i !== index) }));

  // ── Open Add ──────────────────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setEditingId(null);
    setOpenDialog(true);
  };

  // ── Open Edit ─────────────────────────────────────────────────────────────────
  const handleOpenEdit = (notification) => {
    setFormData({
      type: notification.type ?? 1,
      priority: notification.priority ?? 0,
      title: notification.title || "",
      description: notification.description || "",
      image_url: notification.image_url || "",
      cta: Array.isArray(notification.cta) ? notification.cta : [],
      status: notification.status || "ACTIVE",
    });
    setIsEditing(true);
    setEditingId(notification.n_id);
    setOpenDialog(true);
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showSnackbar("Title is required.", "warning");
      return;
    }

    const typeNum = Number(formData.type);

    // Validate image_url required for types 2–5
    if (TYPES_WITH_IMAGE.includes(typeNum) && !formData.image_url.trim()) {
      showSnackbar(`Image URL is required for notification type ${typeNum}.`, "warning");
      return;
    }

    // Validate CTAs for types 4–5
    if (TYPES_WITH_CTA.includes(typeNum)) {
      if (formData.cta.length === 0) {
        showSnackbar(`At least one CTA is required for notification type ${typeNum}.`, "warning");
        return;
      }
      const invalidCta = formData.cta.some(c => !c.cta_title.trim() || !c.cta_url.trim());
      if (invalidCta) {
        showSnackbar("All CTA entries must have a title and URL.", "warning");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        type: Number(formData.type),
        priority: Number(formData.priority),
        // Only send image_url if the type requires it
        image_url: TYPES_WITH_IMAGE.includes(typeNum) ? formData.image_url : undefined,
        // Only send cta if the type requires it
        cta: TYPES_WITH_CTA.includes(typeNum) ? formData.cta : undefined,
      };

      if (isEditing) {
        await api.put(`/api/notifications/${editingId}`, payload);
        showSnackbar("Notification updated successfully!", "success");
      } else {
        await api.post("/api/notifications", payload);
        showSnackbar("Notification created successfully!", "success");
      }
      setOpenDialog(false);
      fetchNotifications();
    } catch (err) {
      console.error("Save failed:", err);
      showSnackbar(err.response?.data?.msg || "Failed to save notification.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Single delete ─────────────────────────────────────────────────────────────
  const handleDeleteClick = (notification) => {
    setDeleteTarget(notification);
    setDeleteDialogOpen(true);
  };

  // ── Bulk delete trigger ───────────────────────────────────────────────────────
  const handleBulkDeleteClick = () => {
    setDeleteTarget(null); // null = bulk
    setDeleteDialogOpen(true);
  };

  // ── Confirm delete (single or bulk) ──────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      if (deleteTarget) {
        // Single delete
        await api.delete(`/api/notifications/${deleteTarget.n_id}`);
        showSnackbar("Notification deleted successfully.", "success");
        setSelected(prev => prev.filter(id => id !== deleteTarget.n_id));
      } else {
        // Bulk delete
        await api.post("/api/notifications/bulk-delete", { ids: selected });
        showSnackbar(`${selected.length} notification(s) deleted successfully.`, "success");
        setSelected([]);
      }
      fetchNotifications();
    } catch (err) {
      console.error("Delete failed:", err);
      showSnackbar(err.response?.data?.msg || "Failed to delete notification(s).", "error");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  // ── Derived UI values ─────────────────────────────────────────────────────────
  const typeNum = Number(formData.type);
  const showImageField = TYPES_WITH_IMAGE.includes(typeNum);
  const showCtaSection = TYPES_WITH_CTA.includes(typeNum);
  const isType5 = typeNum === 5; // Type 5 has exactly one fixed CTA
  const deleteCount = deleteTarget ? 1 : selected.length;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
      {snackbar.open && (
        <SnackBarComponent
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        />
      )}

      {/* ── Main table card ─────────────────────────────────────────────────── */}
      <Paper sx={{ width: "100%", mb: 3 }}>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {selected.length > 0 ? (
            <Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1">
              {selected.length} selected
            </Typography>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" component="div">
                NOTIFICATIONS
              </Typography>
            </Box>
          )}

          {selected.length > 0 ? (
            <Tooltip title={canDelete ? "Delete Selected" : "You do not have permission to delete notifications."}>
              <span>
                <IconButton
                  onClick={handleBulkDeleteClick}
                  color="error"
                  disabled={!canDelete || deleting}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={canCreate ? "" : "You do not have permission to create notifications."}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAdd}
                    disabled={!canCreate}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Add Notification
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="notifications table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < displayedNotifications.length
                    }
                    checked={
                      displayedNotifications.length > 0 &&
                      selected.length === displayedNotifications.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size="1.5rem" />
                  </TableCell>
                </TableRow>
              ) : displayedNotifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">No notifications found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedNotifications.map((n, idx) => {
                  const isItemSelected = isSelected(n.n_id);
                  return (
                    <TableRow
                      key={n.n_id || idx}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => handleRowCheck(e, n.n_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Type ${n.type}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <PriorityChip priority={n.priority} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.title}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.description}
                      </TableCell>
                      <TableCell>{n.status}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={canUpdate ? "Edit" : "You do not have permission to edit notifications."}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(n)}
                              disabled={!canUpdate}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={canDelete ? "Delete" : "You do not have permission to delete notifications."}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(n)}
                              disabled={!canDelete}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredNotifications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? "Edit Notification" : "Add New Notification"}
        </DialogTitle>
        <DialogContent dividers>
          <form id="notification-form" onSubmit={handleSave}>
            <Grid container spacing={2}>

              {/* Row 1 – Type, Priority, Status */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Notification Type"
                  name="type"
                  select
                  value={formData.type}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  required
                >
                  {NOTIFICATION_TYPES.map(t => (
                    <MenuItem key={t} value={t}>Type {t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Priority"
                  name="priority"
                  select
                  value={formData.priority}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  required
                >
                  {Object.entries(PRIORITY_CONFIG).map(([val, { label, color }]) => (
                    <MenuItem key={val} value={Number(val)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Status"
                  name="status"
                  select
                  value={formData.status}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </TextField>
              </Grid>

              {/* Row 2 – Title */}
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  placeholder="e.g. LAUNCH X UNFOLDING"
                />
              </Grid>

              {/* Row 3 – Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  required
                />
              </Grid>

              {/* Row 4 – Image URL (only for types 2–5) */}
              {showImageField && (
                <Grid item xs={12}>
                  <TextField
                    label="Image URL"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required
                    placeholder="https://cdn.example.com/image.jpg"
                    helperText={`Required for notification type ${typeNum}`}
                  />
                </Grid>
              )}

              {/* Row 5 – CTA section (only for types 4–5) */}
              {showCtaSection && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />

                  {isType5 ? (
                    /* ── Type 5: single fixed CTA ─────────────────────────────── */
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                        CTA Button (Type 5 has one fixed CTA)
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="CTA Title"
                              value={formData.cta[0]?.cta_title ?? ""}
                              onChange={(e) => handleCtaChange(0, "cta_title", e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="e.g. Book Now"
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              label="CTA URL"
                              value={formData.cta[0]?.cta_url ?? ""}
                              onChange={(e) => handleCtaChange(0, "cta_url", e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="https://example.com/booking"
                              required
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  ) : (
                    /* ── Type 4: dynamic multi-CTA ────────────────────────────── */
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          CTA Buttons (required for Type {typeNum})
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={addCta}
                        >
                          Add CTA
                        </Button>
                      </Box>

                      {formData.cta.length === 0 && (
                        <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
                          No CTA buttons added yet. Click "Add CTA" to add one.
                        </Typography>
                      )}

                      {formData.cta.map((cta, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 1.5,
                            alignItems: "flex-start",
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={1} sx={{ flex: 1 }}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="CTA Title"
                                value={cta.cta_title}
                                onChange={(e) => handleCtaChange(index, "cta_title", e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="e.g. Book Now"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                              <TextField
                                label="CTA URL"
                                value={cta.cta_url}
                                onChange={(e) => handleCtaChange(index, "cta_url", e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="https://example.com/booking"
                                required
                              />
                            </Grid>
                          </Grid>
                          <Tooltip title="Remove CTA">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeCta(index)}
                              sx={{ mt: 0.5 }}
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </>
                  )}
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            type="submit"
            form="notification-form"
            variant="contained"
            color="primary"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Saving..." : isEditing ? "Update Notification" : "Save Notification"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        selectedCount={deleteCount}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemLabel="notification"
      />
    </Box>
  );
}
